import { Chip as MuiChip, type ChipProps as MuiChipProps } from '@mui/material';
import { forwardRef } from 'react';

/**
 * Semantic statuses used by the Qfly CRM domain.
 *
 * `stage_type` on Opportunity is one of `open | closed_won | closed_lost`;
 * we add `draft` + `neutral` for generic list states so the same Chip covers
 * user + account statuses too.
 */
export type StatusVariant =
  | 'open'
  | 'closed_won'
  | 'closed_lost'
  | 'draft'
  | 'active'
  | 'inactive'
  | 'warning'
  | 'neutral'
  // Dashboard V2 Phase 6 — an admin-published, org-wide-visible widget
  // (`scope='organization'`). Outlined-default like `draft`/`inactive`
  // (a status, not an alert), but its own `info` color so it reads as a
  // distinct, positive "shared" signal rather than "unfinished"/"off".
  | 'org_wide';

export interface StatusChipProps extends Omit<MuiChipProps, 'color' | 'variant'> {
  status: StatusVariant;
  /** Optional override for the label. Defaults to a humanized version of `status`. */
  label?: MuiChipProps['label'];
}

const VARIANT_MAP: Record<
  StatusVariant,
  { color: MuiChipProps['color']; variant: MuiChipProps['variant']; defaultLabel: string }
> = {
  open: { color: 'info', variant: 'filled', defaultLabel: 'Open' },
  closed_won: { color: 'success', variant: 'filled', defaultLabel: 'Closed Won' },
  closed_lost: { color: 'error', variant: 'filled', defaultLabel: 'Closed Lost' },
  draft: { color: 'default', variant: 'outlined', defaultLabel: 'Draft' },
  active: { color: 'success', variant: 'outlined', defaultLabel: 'Active' },
  inactive: { color: 'default', variant: 'outlined', defaultLabel: 'Inactive' },
  warning: { color: 'warning', variant: 'filled', defaultLabel: 'Warning' },
  neutral: { color: 'default', variant: 'outlined', defaultLabel: 'Neutral' },
  org_wide: { color: 'info', variant: 'outlined', defaultLabel: 'Org-wide' },
};

/**
 * Colored status chip. Maps a domain `status` to a consistent palette so every
 * module surfaces the same visual signal for the same underlying state.
 */
export const StatusChip = forwardRef<HTMLDivElement, StatusChipProps>(function StatusChip(
  { status, label, size = 'small', ...rest },
  ref,
) {
  // Fall back to `neutral` when `status` doesn't resolve in the map — e.g. a
  // caller passed a value outside the known enum, or a nullable/unresolved
  // value was cast through. Prevents destructuring `undefined` and throwing.
  const mapping = VARIANT_MAP[status] ?? VARIANT_MAP.neutral;
  return (
    <MuiChip
      ref={ref}
      size={size}
      color={mapping.color}
      variant={mapping.variant}
      label={label ?? mapping.defaultLabel}
      aria-label={`status: ${mapping.defaultLabel}`}
      data-testid={`status-chip-${status}`}
      {...rest}
    />
  );
});
